import { useCallback } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';

import { logErrorWrapper } from '../../../../services/logWrapper';
import {
  selectDay,
  selectDayForHotel,
  selectMonth,
  selectMonthForHotel,
  hasHotelDataOnBothDates,
} from '../../actions/date-select-actions';
import { setInitialSearchParamDates } from '../../actions/initial-search-param-actions';
import {
  getSelectedOutboundDate,
  getSelectedInboundDate,
  getSelectedOutboundMonth,
} from '../../selectors';
import * as analyticsConstants from '../constants/analyticsConstants';
import * as constants from '../constants/dataTypeConstants';
import dateValidationDispatcher from '../dateValidationDispatcher';

import type {
  DateSelectDaySource,
  VerticalType,
} from '../../actions/date-select-actions';
import type { Direction, State } from '../../selectors/types';

export const useDateSelection = () => {
  const dispatch = useDispatch();
  const store = useStore<State>();
  const selectedOutbound = useSelector(getSelectedOutboundDate);
  const selectedInbound = useSelector(getSelectedInboundDate);
  const selectedMonth = useSelector(getSelectedOutboundMonth);

  const syncHotelDataOnBothDates = useCallback(() => {
    dispatch(hasHotelDataOnBothDates(store.getState()));
  }, [dispatch, store]);

  const selectDayAction = useCallback(
    async (
      dayOfMonth: number,
      direction: Direction,
      source: DateSelectDaySource,
      vertical: VerticalType,
    ) => {
      const isHotel = vertical === analyticsConstants.HOTEL_VERTICAL;
      const isFlight = vertical === analyticsConstants.FLIGHT_VERTICAL;

      try {
        dispatch(
          selectDay(
            dayOfMonth,
            direction,
            isFlight ? source : constants.AUTOMATED_SOURCE,
          ),
        );

        dispatch(
          selectDayForHotel(
            dayOfMonth,
            direction,
            isHotel ? source : constants.AUTOMATED_SOURCE,
          ),
        );

        syncHotelDataOnBothDates();
      } catch (error: unknown) {
        logErrorWrapper(error as Error, {
          message: 'triggerSelectDateActionChain error',
          component: 'useDateSelection',
        });
      }
    },
    [dispatch, syncHotelDataOnBothDates],
  );

  const selectMonthAction = useCallback(
    async (
      month: number,
      year: number,
      direction: Direction,
      source: string,
    ) => {
      try {
        dispatch(selectMonth(month, year, direction, source));

        dispatch(selectMonthForHotel(month, year, direction, source));

        dateValidationDispatcher.ensureValidHotelDates(
          store.getState(),
          dispatch,
        );

        syncHotelDataOnBothDates();
      } catch (error: unknown) {
        logErrorWrapper(error as Error, {
          message: 'triggerSelectMonthActionChain error',
          component: 'useDateSelection',
        });
      }
    },
    [dispatch, store, syncHotelDataOnBothDates],
  );

  const selectMonthsFromDates = useCallback(
    (outboundDate: Date, inboundDate: Date | undefined, isReturn: boolean) => {
      const outboundMonthNum = outboundDate.getMonth() + 1;
      const outboundYear = outboundDate.getFullYear();

      dispatch(setInitialSearchParamDates(outboundMonthNum, outboundYear));

      dispatch(
        selectMonth(
          outboundMonthNum,
          outboundYear,
          constants.OUTBOUND_DIRECTION,
          constants.AUTOMATED_SOURCE,
        ),
      );

      dispatch(
        selectMonthForHotel(
          outboundMonthNum,
          outboundYear,
          constants.OUTBOUND_DIRECTION,
          constants.AUTOMATED_SOURCE,
        ),
      );
      dispatch(
        selectMonthForHotel(
          outboundMonthNum,
          outboundYear,
          constants.INBOUND_DIRECTION,
          constants.AUTOMATED_SOURCE,
        ),
      );

      if (isReturn && inboundDate) {
        const inboundMonthNum = inboundDate.getMonth() + 1;
        const inboundYear = inboundDate.getFullYear();
        dispatch(
          selectMonth(
            inboundMonthNum,
            inboundYear,
            constants.INBOUND_DIRECTION,
            constants.AUTOMATED_SOURCE,
          ),
        );
        dispatch(
          selectMonthForHotel(
            inboundMonthNum,
            inboundYear,
            constants.INBOUND_DIRECTION,
            constants.AUTOMATED_SOURCE,
          ),
        );
      }
    },
    [dispatch],
  );

  return {
    selectedOutbound,
    selectedInbound,
    selectedMonth,
    selectDay: selectDayAction,
    selectMonth: selectMonthAction,
    syncHotelDataOnBothDates,
    selectMonthsFromDates,
  };
};
